import React, { useState, Fragment } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  IconButton,
  Popover,
  Tooltip,
} from "@mui/material";

import InfoIcon from "@mui/icons-material/Info";
import { FractionalCalculator } from "../../components/FractionalCalculator";
const FIELD_EXTRAS = {
    SHEEN_GUIDE: "sheen_guide",
    GLASS_INSERT_GUIDE: "glass_insert_guide",
    FRACTIONAL_CALCULATOR: "fraction_calculator",
    CUSTOM_HINGE_OFFSET: "custom_hinge_offset",
    TOOLTIP: "tooltip"
};
  
const makeExtra = (element, field) => {
    switch (field.extra) {
      case FIELD_EXTRAS.TOOLTIP:
        return (
          <PopoverComponent element={element} content={
            <div style={{maxWidth: 300, padding: 8, textAlign: "center"}}>{field.tooltip || ""}</div>
          } />
        )
      case FIELD_EXTRAS.SHEEN_GUIDE:
        return (
          <PopoverComponent
            element={element}
            content={
              <Fragment>
                <TableContainer component={Paper}>
                  <Table sx={{ minWidth: 20 }} size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          Sheen Guide
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>Sheen 10</TableCell>
                        <TableCell>Eggshell</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Sheen 20 - 30</TableCell>
                        <TableCell>Satin</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Sheen 40 - 50</TableCell>
                        <TableCell>SemiGloss</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Sheen 60</TableCell>
                        <TableCell>Gloss</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Fragment>
            }
          />
        );
      case FIELD_EXTRAS.GLASS_INSERT_GUIDE:
        return (
          <PopoverComponent
            element={element}
            content={
              <Typography sx={{ p: 2 }}>
                If Glass Inset is selected, here is the size of the glass you
                <br />
                need to order. This measurement includes a 1/16th reduction
                <br />
                allowance for proper fitment of the glass panel.
                <br />
                <br />
                N/A for Euro Slab Doors.
              </Typography>
            }
          />
        );
      case FIELD_EXTRAS.CUSTOM_HINGE_OFFSET:
        return (
          <PopoverComponent
            element={element}
            content={
              <Typography sx={{ p: 2 }}>
                If you specified Custom Hinge Boring Offset,<br />
                enter the offset here (in millimeters).<br />
                Distance is measured from top and bottom edge<br />
                of the cabinet door to the center of the<br />
                hinge bore.
              </Typography>
            }
          />
        );
      case FIELD_EXTRAS.FRACTIONAL_CALCULATOR:
        return (
          <PopoverComponent
            element={element}
            content={
              <Typography sx={{ p: 2}}>
                Enter measurements as
                fractional or decimal.
              </Typography>
            }
          />
        );
      default:
        return element;
    }
};

const PopoverComponent = ({ element, content }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const id = open ? "simple-popover" : undefined;

    return (
        <div style={{ display: "flex", alignItems: "center", padding: 0, margin: 0 }}>
        {element}
        <IconButton
            size={"small"}
            onClick={(event) => {
            setAnchorEl(event.currentTarget);
            }}
        >
            <InfoIcon />
        </IconButton>
        <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{
            vertical: "top",
            horizontal: "center",
            }}
            transformOrigin={{
            vertical: "bottom",
            horizontal: "center",
            }}
            PaperProps={{
            elevation: 1,
            }}
        >
            {content}
        </Popover>
        </div>
    );
};

const makeTextLabel = (label) => {
    if (
      label ===
      "Custom Color Sherwin Williams Code (N/A if You've Already Selected Primed or Standard Colors)"
    ) {
      return makeTextLabel("Custom Color\nSherwin Williams Code");
    }
    if (label.length > 16 && label.includes("(") && !label.includes("(s)")) {
      return makeTextLabel(
        label.split("(")[0] + "\n" + label.split("(")[1].replace(")", "")
      );
    }
    return (
      <div>
        {label.split("\n").filter((l) => l.trim() !== "" && l.trim() !== "-").map((text, idx) => (
          <Typography noWrap key={idx} sx={{ fontSize: idx > 0 ? 10.0 : 14.0 }}>
            {text}
          </Typography>
        ))}
      </div>
    );
};

function makeLabelRow(field, row) {
  var label = field.label;
  if (field.label.length > 16 && field.label.includes("(") && !field.label.includes("(s)")) {
    label = label.split("(")[0] + "\n" + label.split("(")[1].replace(")", "");
  }

  let rows = label.split("\n").filter((l) => l.trim() !== "" && l.trim() !== "-");
  if(field.info_text !== "") {
    rows.push(field.info_text);
  }
  if(rows.length > row) {
    let textLabel = row > 0 ?
      <Typography sx={{ fontSize: row > 0 ? 10.0 : 14.0 }}>{rows[row]}</Typography>
      :
      <Typography noWrap sx={{ fontSize: row > 0 ? 10.0 : 14.0 }}>{rows[row]}</Typography>;
    if (field.extra && row === 0) {
      return makeExtra(textLabel, field);
    }
    return textLabel;
  }
  return <></>;
};


const makeLabel = (field) => {
    let textLabel = makeTextLabel(field.label);
    if (field.extra) {
      return makeExtra(textLabel, field);
    }
    return textLabel;
};

const makeShortLabel = (label) => {
    return label.split("\n")[0].split(" (")[0];
};

const ProfilePreviewImage = ({name}) => {
    return <img
      alt={name}
      src={`${process.env.REACT_APP_API_URL}v1/tool/png/${name}/18`}
      width={18}
      height={18}
      style={{padding: 0, marginRight: "8px", marginTop: 0, marginBottom: 0}}/>;
}
  
const POOptionItem = ({field, option}) => {
    if(field.key.toLowerCase().includes("profile")) {
      return <><ProfilePreviewImage name={option.value.replaceAll("/", "_")} />{option.label}</>
    } else {
      return <>{option.label}</>
    }
}
  

export { PopoverComponent, makeExtra, makeLabel, makeLabelRow, makeShortLabel, makeTextLabel, ProfilePreviewImage, POOptionItem }